import { useUtils } from "@/app/hooks/useUtils";
import clsx from "clsx";
import { chunk, isArray } from "lodash";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { MdArrowUpward } from "react-icons/md";
import {
  BlockOverlappedDetailsType,
  BlockPositionType,
  NoShiftReducedStaffDataType,
  ReducedStaffDataType,
  TravelBufferReducedStaffDataType,
} from "../interface";

import { IStaffCalendarFilterData } from "@/api_framework/api_modals/Calendar";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useAfterFirstRender } from "@/app/hooks/useAfterFirstRender";
import { useLoader } from "@/app/hooks/useLoader";
import { differenceInMinutes, parse } from "date-fns";
import { Tooltip } from "react-tooltip";

interface ICalendarTodaysViewComponent {
  slideIndex: number;
  expandedView: boolean;
  calendarData: IStaffCalendarFilterData[];
  stickyTopOffset?: number;
  setReducedStaffData: any;
  reducedStaffData: any;
  setOverlappedBlocks: any;
  overlappedBlocks: any;
}

const CalendarTodaysViewComponent: React.FC<ICalendarTodaysViewComponent> = ({
  slideIndex,
  calendarData,
  stickyTopOffset = 0,
  overlappedBlocks,
  reducedStaffData,
  setOverlappedBlocks,
  setReducedStaffData,
}) => {
  const SLOT_MINUTES = 10;
  const TOTAL_TIME_ROWS = (24 * 60) / SLOT_MINUTES;
  const { setVisible, setModalData } = useModalActions("CALENDAR_BLOCK_DETAILS_MODAL");
  const [providersData, setProvidersData] = useState<IStaffCalendarFilterData[]>([]);
  const [calendarRendered, setCalendarRendered] = useState<boolean>(false);
  const [timeRows, setTimeRows] = useState<Array<string>>([]);
  const totalProviderColumns = 6;
  const stickyTimeHeaderHeight = 64;
  const timeAnchorOffset = stickyTopOffset + stickyTimeHeaderHeight + 14;
  const tableViewportOffset = stickyTopOffset + stickyTimeHeaderHeight + 36;
  const calendarGridColumnsClass =
    "grid-cols-[56px_repeat(6,minmax(104px,1fr))] sm:grid-cols-[72px_repeat(6,minmax(132px,1fr))] lg:grid-cols-[92px_repeat(6,minmax(0,1fr))]";
  const calendarGridMinWidthClass = "min-w-[680px] sm:min-w-[864px] lg:min-w-[1050px]";
  const { backgroundColorBeHalfOfService, statusButtonColor, convertTo12HourFormatWithAMPM, truncateString } =
    useUtils();
  const container = useRef<HTMLDivElement | null>(null);
  const headerScrollRef = useRef<HTMLDivElement | null>(null);
  const hasAutoScrolledToMorningRef = useRef(false);
  const isCalendarLoading = useLoader("isStaffCalendarLoadingInProgress");

  const [reducedNoWorkStaffData, setReducedNoWorkStaffData] = useState<NoShiftReducedStaffDataType | null>(null);
  const [reducedTravelBufferData, setReducedTravelBufferData] = useState<TravelBufferReducedStaffDataType | null>(
    null,
  );

  const getTimeRows = () => {
    const timings: Array<string> = [];
    Array.from(Array(TOTAL_TIME_ROWS), (_, i) => {
      const totalMinutes = i * SLOT_MINUTES;
      const hour = Math.floor(totalMinutes / 60);
      const minute = totalMinutes % 60;
      const hourDisplay = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const amPm = hour < 12 ? "AM" : "PM";
      timings.push(`${String(hourDisplay)}:${String(minute).padStart(2, "0")} ${amPm}`);
    });
    return timings;
  };
  function calculateTimeDifference(startTimeStr: string, endTimeStr: string, addPadding?: number) {
    const format = "HH:mm";
    const parsedStartTime = parse(startTimeStr, format, new Date());
    const parsedEndTime = parse(endTimeStr, format, new Date());

    if (parsedEndTime < parsedStartTime) {
      parsedEndTime.setDate(parsedEndTime.getDate() + 1);
    }

    const diffInMinutes = differenceInMinutes(parsedEndTime, parsedStartTime);
    const timeSpanBlocks = Math.ceil(diffInMinutes / SLOT_MINUTES);

    return {
      diffMin: diffInMinutes,
      timeSpanBlocks: timeSpanBlocks + (addPadding ?? 0),
    };
  }

  const calculateBlockPosition = (startTimeStr: string, endTimeStr: string): BlockPositionType => {
    const fromMidnightDiff = calculateTimeDifference("00:00", startTimeStr);
    const slotTimingDiff = calculateTimeDifference(startTimeStr, endTimeStr);
    return {
      rowPos: Math.round(fromMidnightDiff.timeSpanBlocks),
      columnPos: 2,
      rowSpan: slotTimingDiff.timeSpanBlocks + 1,
      columnSpan: 1,
    };
  };

  useEffect(() => {
    const chunkedData = chunk(calendarData, 6);
    setProvidersData(chunkedData[slideIndex]);
  }, [calendarData]);

  const returnValidTimeStr = (timeStr: string) => {
    const t = timeStr.split(":");
    let newTime: string = "";
    if (t.length > 2) {
      newTime = `${t[0]}:${t[1]}`;
    } else {
      newTime = timeStr;
    }
    return newTime;
  };

  const groupOverlappingBlocks = (blocks: BlockPositionType[]): Array<BlockPositionType[]> => {
    const groupedData = blocks.reduce((acc, obj) => {
      const { columnPos } = obj;
      if (!acc[columnPos]) {
        acc[columnPos] = [];
      }

      // Check if there is a group where rowPos and columnPos are exactly the same
      let foundGroup = false;
      for (const group of acc[columnPos]) {
        if (group[0].rowPos === obj.rowPos && group[0].columnPos === obj.columnPos) {
          group.push(obj);
          foundGroup = true;
          break;
        } else if (obj.rowPos > group[0].rowPos && obj.rowPos < group[0].rowPos + group[0].rowSpan - 2) {
          group.push(obj);
          foundGroup = true;
          break;
        }
      }

      // If no existing group, create a new group
      if (!foundGroup) {
        acc[columnPos].push([obj]);
      }

      return acc;
    }, {});

    const reducedGroups = Object.values(groupedData).flatMap((groups: Array<BlockPositionType[]>) =>
      groups.filter((group) => group.length >= 2),
    );
    return reducedGroups;
  };

  // Function to add minutes to a time string in HH:MM format
  // Helper function to format time as HH:MM
  function formatTime(timeStr) {
    const [hours, minutes] = timeStr.split(":").map((num) => parseInt(num, 10));
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  }

  const noShiftDataReducer = () => {
    return providersData?.reduce((final, current, currentIndex) => {
      if (current?.staff_schedule) {
        // Split the duty schedule into start and end times, and ensure they are formatted properly
        const staffSchedule = current.staff_schedule.split(",").map((schedule) => {
          const [start, end] = schedule.split("to").map((time) => formatTime(time.trim())); // Format the time
          return { start, end };
        });

        // Check if there are slot details
        const hasSlots = current.slot_details?.length > 0;

        if (hasSlots) {
          // If there are slot details, map and sort them by start time
          const sortedSlots = current.slot_details
            ?.map((slot) => ({
              start: formatTime(returnValidTimeStr(slot.booking_start_time)),
              end: formatTime(returnValidTimeStr(slot.booking_end_time)),
            }))
            ?.sort((a, b) => a.start.localeCompare(b.start));

          // Combine staff duty schedule and slot times, then sort by start time
          const dutyAndSlotBlocks = [...staffSchedule, ...sortedSlots].sort((a, b) => a.start.localeCompare(b.start));

          // Merge overlapping or adjacent blocks
          const mergedBlocks = [];
          let currentBlock = dutyAndSlotBlocks[0];

          for (let i = 1; i < dutyAndSlotBlocks.length; i++) {
            const nextBlock = dutyAndSlotBlocks[i];
            if (currentBlock.end >= nextBlock.start) {
              // Merge blocks if they overlap or are adjacent
              currentBlock = {
                start: currentBlock.start,
                end: nextBlock.end > currentBlock.end ? nextBlock.end : currentBlock.end,
              };
            } else {
              mergedBlocks.push(currentBlock);
              currentBlock = nextBlock;
            }
          }
          mergedBlocks.push(currentBlock);

          // Create grey blocks for gaps between merged duty/booking blocks
          for (let i = 1; i < mergedBlocks.length; i++) {
            const gapStart = mergedBlocks[i - 1].end;
            const gapEnd = mergedBlocks[i].start;

            if (gapStart < gapEnd) {
              final[`${current.staff_id}-gap-${i}`] = {
                blockPos: {
                  ...calculateBlockPosition(gapStart, gapEnd),
                  columnPos: currentIndex + 2,
                  blockId: `no-schedule-block-gap-${current.staff_id}-${i}`,
                },
              };
            }
          }

          // Before first duty block (00:00 to first block start)
          if ("00:00" < mergedBlocks[0].start) {
            final[`${current.staff_id}-before`] = {
              blockPos: {
                ...calculateBlockPosition("00:00", mergedBlocks[0].start),
                columnPos: currentIndex + 2,
                blockId: `no-schedule-block-before-${current.staff_id}`,
              },
            };
          }

          // After last duty block (last block end to end of day)
          if (mergedBlocks[mergedBlocks.length - 1].end < "23:50") {
            final[`${current.staff_id}-after`] = {
              blockPos: {
                ...calculateBlockPosition(mergedBlocks[mergedBlocks.length - 1].end, "23:50"),
                columnPos: currentIndex + 2,
                blockId: `no-schedule-block-after-${current.staff_id}`,
              },
            };
          }
        } else {
          const sortedSchedule = [...staffSchedule].sort((a, b) => a.start.localeCompare(b.start));
          const firstDuty = sortedSchedule[0];
          const lastDuty = sortedSchedule[sortedSchedule.length - 1];

          // Before first duty block (00:00 to first block start)
          if ("00:00" < firstDuty.start) {
            final[`${current.staff_id}-before`] = {
              blockPos: {
                ...calculateBlockPosition("00:00", firstDuty.start),
                columnPos: currentIndex + 2,
                blockId: `no-schedule-block-before-${current.staff_id}`,
              },
            };
          }

          // After last duty block (last block end to 23:50)
          if (lastDuty.end < "23:50") {
            final[`${current.staff_id}-after`] = {
              blockPos: {
                ...calculateBlockPosition(lastDuty.end, "23:50"),
                columnPos: currentIndex + 2,
                blockId: `no-schedule-block-after-${current.staff_id}`,
              },
            };
          }

          // Gaps between schedule blocks
          for (let i = 1; i < sortedSchedule.length; i++) {
            const prev = sortedSchedule[i - 1];
            const curr = sortedSchedule[i];

            if (prev.end < curr.start) {
              final[`${current.staff_id}-gap-${i}`] = {
                blockPos: {
                  ...calculateBlockPosition(prev.end, curr.start),
                  columnPos: currentIndex + 2,
                  blockId: `no-schedule-block-gap-${current.staff_id}-${i}`,
                },
              };
            }
          }
        }
      }
      return final;
    }, {}) as NoShiftReducedStaffDataType;
  };

  const travelBufferReducer = () => {
    return providersData?.reduce((final, current, currentIndex) => {
      current?.slot_details?.forEach((slot) => {
        const preStart = slot?.pre_booking_travel_buffer_start;
        const preEnd = slot?.pre_booking_travel_buffer_end;
        const postStart = slot?.post_booking_travel_buffer_start;
        const postEnd = slot?.post_booking_travel_buffer_end;

        if (preStart && preEnd) {
          final[`${current.staff_id}-pre-buffer-${slot.booking_id}`] = {
            blockPos: {
              ...calculateBlockPosition(returnValidTimeStr(preStart), returnValidTimeStr(preEnd)),
              columnPos: currentIndex + 2,
              blockId: `travel-buffer-pre-${slot.booking_id}`,
            },
          };
        }

        if (postStart && postEnd) {
          final[`${current.staff_id}-post-buffer-${slot.booking_id}`] = {
            blockPos: {
              ...calculateBlockPosition(returnValidTimeStr(postStart), returnValidTimeStr(postEnd)),
              columnPos: currentIndex + 2,
              blockId: `travel-buffer-post-${slot.booking_id}`,
            },
          };
        }
      });
      return final;
    }, {}) as TravelBufferReducedStaffDataType;
  };

  const dataReducer = () => {
    return providersData?.reduce((final, current, currentIndex) => {
      // Map normal booking slots
      const mappedSlots = current?.slot_details
        ? current.slot_details.map((c) => ({
            endTime: c?.booking_end_time,
            startTime: c?.booking_start_time,
            providerName: isArray(c?.staff_details) ? c?.staff_details[0]?.name : c?.staff_details?.name,
            bookingStatus: c?.booking_status,
            userDetails: c?.user_details ?? { id: "", name: "", mobile: "" },
            bookingId: c?.booking_id,
            petDetails: c?.pet_details ?? { id: "", name: "", pet_breed: "", pet_species_name: "" },
            subserviceDetails: c?.subservice_details ?? { id: "", name: "", service_type: "" },
            serviceId: c?.service_details?.id ?? "",
            subservice_id: c?.subservice_id ?? "",
            blockPos: {
              ...calculateBlockPosition(returnValidTimeStr(c.booking_start_time), returnValidTimeStr(c.booking_end_time)),
              columnPos: currentIndex + 2,
              blockId: `block-${c?.booking_id}`,
            },
          }))
        : [];

      final[current.staff_id] = mappedSlots;
      return final;
    }, {} as ReducedStaffDataType);
  };

  const checkOverlappingOfBlock = (blockId: string): BlockOverlappedDetailsType => {
    const f = overlappedBlocks.filter((blockArr) => {
      return blockArr?.filter((block) => block?.blockId === blockId).length > 0;
    });
    if (f.length > 0) {
      const overlappedBlock = document.getElementById(f[0][0]?.blockId);
      return {
        overlapping: true,
        overlappedBlockRect: overlappedBlock?.getBoundingClientRect(),
        overlappedBlockId: f[0][0]?.blockId,
        overlappedBlock: f[0][0],
        totalOverlapped: f[0]?.length,
      };
    } else {
      return {
        overlapping: false,
        overlappedBlockRect: null,
        overlappedBlockId: null,
        overlappedBlock: null,
        totalOverlapped: 0,
      };
    }
  };

  useEffect(() => {
    const timeRows = getTimeRows();
    setTimeRows(timeRows);
    const reducedData = dataReducer();
    setReducedStaffData(reducedData);

    const noShiftData = noShiftDataReducer();
    setReducedNoWorkStaffData(noShiftData);

    const travelBufferData = travelBufferReducer();
    setReducedTravelBufferData(travelBufferData);

    // Set overlapping blocks once the data has been reduced.
    if (reducedData) {
      const blocksPositions = Object.values(reducedData)
        .flat()
        .map((block) => block.blockPos);
      const filteredOverlappingBlocks = groupOverlappingBlocks(blocksPositions)?.filter(
        (blockArray) => blockArray.length >= 2,
      );
      setOverlappedBlocks(filteredOverlappingBlocks);
    }
  }, [providersData]);

  useAfterFirstRender(() => {
    const chunked = chunk(calendarData, 6);
    setProvidersData(chunked[slideIndex]);
  }, [slideIndex]);

  useEffect(() => {
    if (calendarRendered) return;
    if (!isCalendarLoading) {
      setCalendarRendered(true);
    }
  }, [document.getElementsByClassName("calendar-block").length, reducedStaffData]);


  const convertTimeLabelToMinutes = (timeLabel: string): number | null => {
    if (!timeLabel) return null;
    const [rawTime, rawMeridiem] = timeLabel.trim().split(" ");
    if (!rawTime || !rawMeridiem) return null;

    const [hourPart, minutePart] = rawTime.split(":");
    const parsedHour = Number(hourPart);
    const parsedMinute = Number(minutePart);
    if (Number.isNaN(parsedHour) || Number.isNaN(parsedMinute)) return null;

    const upperMeridiem = rawMeridiem.toUpperCase();
    if (upperMeridiem !== "AM" && upperMeridiem !== "PM") return null;

    let hour24 = parsedHour % 12;
    if (upperMeridiem === "PM") hour24 += 12;

    return hour24 * 60 + parsedMinute;
  };

  const findClosestTimeRow = (requestedTimeLabel: string): string | null => {
    const requestedMinutes = convertTimeLabelToMinutes(requestedTimeLabel);
    if (requestedMinutes === null || timeRows.length === 0) return null;

    let closestTime = timeRows[0];
    let smallestDiff = Number.MAX_SAFE_INTEGER;

    timeRows.forEach((rowTime) => {
      const rowMinutes = convertTimeLabelToMinutes(rowTime);
      if (rowMinutes === null) return;
      const diff = Math.abs(rowMinutes - requestedMinutes);
      if (diff <= smallestDiff) {
        smallestDiff = diff;
        closestTime = rowTime;
      }
    });

    return closestTime;
  };

  const scrollToTimeTarget = useCallback((timeLabel: string, smooth = true) => {
    const directTarget = document.getElementById(timeLabel);
    const fallbackTargetTime = !directTarget ? findClosestTimeRow(timeLabel) : null;
    const target = directTarget ?? (fallbackTargetTime ? document.getElementById(fallbackTargetTime) : null);
    if (!target) return;
    if (!container.current) return;
    const parentRect = container.current.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const scrollTop = container.current.scrollTop + (targetRect.top - parentRect.top) - 4;

    container.current.scrollTo({
      top: Math.max(scrollTop, 0),
      behavior: smooth ? "smooth" : "auto",
    });
  }, [timeRows]);

  useEffect(() => {
    const handleQuickFilterScroll = (event: Event) => {
      const scrollEvent = event as CustomEvent<{ timeLabel?: string }>;
      const timeLabel = scrollEvent?.detail?.timeLabel;
      if (!timeLabel) return;
      scrollToTimeTarget(timeLabel, true);
    };

    window.addEventListener("staff-calendar:scroll-to-time", handleQuickFilterScroll as EventListener);
    return () => window.removeEventListener("staff-calendar:scroll-to-time", handleQuickFilterScroll as EventListener);
  }, [scrollToTimeTarget]);

  useEffect(() => {
    if (hasAutoScrolledToMorningRef.current) return;
    if (isCalendarLoading) return;
    if (!container.current || timeRows.length === 0) return;

    const raf = window.requestAnimationFrame(() => {
      scrollToTimeTarget("8:30 AM", false);
      hasAutoScrolledToMorningRef.current = true;
    });

    return () => window.cancelAnimationFrame(raf);
  }, [isCalendarLoading, timeRows, scrollToTimeTarget]);

  return (
    <div>
      <div className="fixed bottom-4 right-4 z-50 sm:bottom-10 sm:right-10">
        <button
          type="button"
          className="w-11 h-11 sm:w-10 sm:h-10 bg-slate-700 items-center justify-center flex rounded-xl shadow-lg hover:scale-110 transition-transform duration-500 cursor-pointer"
          onClick={() => {
            scrollToTimeTarget("9:00 AM");
          }}
        >
          <span>
            <MdArrowUpward className="w-6 h-6 text-white" />
          </span>
        </button>
      </div>
      <div className="bg-white">
        <div className="relative mt-1 rounded-xl border border-gray-200 bg-white shadow-sm sm:rounded-2xl">
          <div
            className="sticky z-20 w-full overflow-hidden rounded-t-xl sm:rounded-t-2xl border-b border-gray-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/90"
            style={{ top: stickyTopOffset }}
          >
            <div
              ref={headerScrollRef}
              className="w-full overflow-x-auto calendar-hide-scrollbar"
              onScroll={(event) => {
                if (!container.current) return;
                const target = event.currentTarget;
                if (container.current.scrollLeft !== target.scrollLeft) {
                  container.current.scrollLeft = target.scrollLeft;
                }
              }}
            >
              <div className={clsx("grid bg-[#f8efe1]", calendarGridColumnsClass, calendarGridMinWidthClass)}>
              <div className="flex min-h-[64px] items-center justify-center border-r border-b border-gray-200 px-1 text-[10px] font-semibold uppercase tracking-wide text-gray-700 sm:px-2 sm:text-[11px]">
                Time
              </div>
              {providersData?.map((d, idx) => {
                return (
                  <div
                    className="min-h-[64px] border-r border-b border-gray-200 bg-white/80 px-2 py-2 sm:px-3"
                    key={idx}
                  >
                    <div className="flex items-start justify-between gap-1 sm:gap-2">
                      <p className="truncate text-[11px] font-semibold text-gray-800 sm:text-xs"

                        data-tooltip-id="doctorName"
                        data-tooltip-content={d?.staff_name}>
                        {truncateString(d?.staff_name, 20)}
                      </p>
                      <span className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-amber-700">
                        {d?.slot_details?.length ?? 0}
                      </span>
                    </div>
                    <p className="mt-1 truncate text-[10px] text-green-700 sm:text-[11px]"

                      data-tooltip-id="doctorShift"
                      data-tooltip-content={d?.staff_schedule}>

                      {truncateString(d?.staff_schedule ?? "NA", 30)}
                    </p>
                  </div>
                );
              })}
              {Array.from(
                { length: Math.max(0, totalProviderColumns - (providersData?.length ?? 0)) },
                (_, idx) => idx,
              ).map((cellIndex) => (
                <div
                  key={`empty-provider-header-${cellIndex}`}
                  className="min-h-[64px] border-r border-b border-gray-200 bg-white/60"
                ></div>
              ))}
              </div>
            </div>
          </div>
          {/* Grey out staff starting blocks */}

          <div className="relative rounded-b-xl sm:rounded-b-2xl">
            <div
              className="overflow-x-auto overflow-y-auto calendar-hide-scrollbar"
              style={{
                maxHeight: `calc(100dvh - ${tableViewportOffset}px)`,
              }}
              ref={container}
              onScroll={(event) => {
                const target = event.currentTarget;
                if (!headerScrollRef.current) return;
                if (headerScrollRef.current.scrollLeft !== target.scrollLeft) {
                  headerScrollRef.current.scrollLeft = target.scrollLeft;
                }
              }}
            >
              <div
                className={clsx(
                  "relative grid",
                  calendarGridColumnsClass,
                  calendarGridMinWidthClass,
                )}
              >
                {reducedNoWorkStaffData &&
                  Object.keys(reducedNoWorkStaffData)?.map((providerId) => {
                    const block = reducedNoWorkStaffData[providerId]?.blockPos;
                    return (
                      <div
                        key={block.blockId}
                        id={block.blockId}
                        style={{
                          gridArea: `${block.rowPos + 1}/${block?.columnPos}/span ${block.rowSpan - 1}/ span 1 `,
                        }}
                        className=" absolute w-full h-full bg-gray-400 opacity-25   "
                      ></div>
                    );
                  })}
                {reducedTravelBufferData &&
                  Object.keys(reducedTravelBufferData)?.map((key) => {
                    const block = reducedTravelBufferData[key]?.blockPos;
                    return (
                      <div
                        key={block.blockId}
                        id={block.blockId}
                        style={{
                          gridArea: `${block.rowPos + 1}/${block?.columnPos}/span ${block.rowSpan - 1}/ span 1 `,
                        }}
                        className="pointer-events-none absolute w-full h-full bg-gray-400 opacity-25"
                      ></div>
                    );
                  })}
                {reducedStaffData &&
                  Object.keys(reducedStaffData)?.map((providerId) => {
                    return reducedStaffData[providerId]?.map((block) => {
                      const overlappingDetails = checkOverlappingOfBlock(block?.blockPos?.blockId);
                      return (
                        <div
                          key={block.blockPos.blockId}
                          id={block?.blockPos?.blockId}
                          style={{
                            gridArea: `${block?.blockPos?.rowPos + 1}/${block?.blockPos?.columnPos}/span ${block.blockPos.rowSpan - 1}/ span 1 `,
                          }}
                          data-row-position={`${block?.blockPos?.rowPos + 1}`}
                          data-col-position={`${block?.blockPos?.columnPos}`}
                          data-row-span={`${block.blockPos.rowSpan - 1}`}
                          className={clsx(
                            `absolute calendar-block p-0.5 sm:p-1 h-full transition-all bg-opacity-100 cursor-pointer overflow-y-auto calendar-hide-scrollbar`,
                            overlappingDetails?.overlapping ? `w-1/2 text-xs` : "w-full",
                            overlappingDetails?.overlappedBlockId === block?.blockPos?.blockId && ` left-1/2`,
                          )}
                          onClick={() => {
                            setModalData({
                              bookingId: block?.bookingId,
                            });
                            setVisible();
                          }}
                        >
                          <div
                            className={clsx(
                              "h-full w-full overflow-y-auto calendar-hide-scrollbar rounded-md shadow-md cursor-pointer transition-transform p-0.5 sm:p-1",
                              backgroundColorBeHalfOfService(block.serviceId),
                            )}
                          >
                            <button
                              className={`${statusButtonColor(block?.bookingStatus?.toLowerCase())}  max-w-full truncate font-semibold rounded-md border text-[9px] px-1 sm:text-[10px] sm:px-2 capitalize`}
                            >
                              {block?.bookingStatus}
                            </button>
                            <p className="font-medium text-[10px] sm:text-xs leading-tight mt-0.5 sm:mt-1">
                              {convertTo12HourFormatWithAMPM(block?.startTime)} -{" "}
                              {convertTo12HourFormatWithAMPM(block?.endTime)}
                            </p>
                            <p className="font-medium truncate text-[10px] sm:text-xs leading-tight mt-0.5 sm:mt-1">
                              {block?.userDetails?.name}
                            </p>
                            <p className="font-medium truncate text-[10px] sm:text-xs leading-tight mt-0.5 sm:mt-1">
                              {block?.petDetails?.name}
                            </p>
                            <p className="font-medium truncate text-[10px] sm:text-xs leading-tight mt-0.5 sm:mt-1">
                              {block?.subserviceDetails?.name}
                            </p>
                          </div>
                        </div>
                      );
                    });
                  })}

                {timeRows?.map((time, idx) => {
                  return (
                    <div key={`${time}:${idx}`} className={clsx("col-span-7 grid", calendarGridColumnsClass)} id={idx.toString()}>
                      <div
                        id={`${time}`}
                        className="flex h-16 items-center justify-center border-r border-b border-gray-200 px-1 text-[10px] font-medium text-gray-700 sm:px-2 sm:text-[11px]"
                        style={{ scrollMarginTop: `${timeAnchorOffset}px` }}
                      >
                        {time}
                      </div>
                      {Array.from(Array(totalProviderColumns), (_, i) => i)?.map((index) => (
                        <div key={index} className="border-r border-b border-gray-100 bg-white"></div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          {/* <Tooltip id="doctorName" place="bottom" /> */}
          <Tooltip id="doctorShift" place="bottom" />
        </div>
      </div>
    </div>
  );
};

export default CalendarTodaysViewComponent;
